import type { Dispatch, SetStateAction } from 'react';
import { useMemo } from 'react';
import { PlusCircle, X } from 'react-feather';
import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Skeleton from '@mui/material/Skeleton';
import { AuraChatContainer } from '@trinity/chat-components/components/BrandInsights/AuraChat.Container.js';
import {
  ChatContextSelectorVisibleContext,
  ChatHomeButtonVisibleContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context.js';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat.js';
import {
  ChatInputFeaturesContext,
  ChatLandingCardsVisibleContext,
} from '@trinity/chat-components/components/Chat/context.js';
import { ChatHeader } from '@trinity/chat-components/components/Chat.Header/index.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { useAppSelector } from '@/store';

const noop: Dispatch<SetStateAction<boolean>> = () => {};

/**
 * Presentation component for the native chat sidebar.
 *
 * Expects `ChatContextProviders` (or equivalent) to be mounted above it so
 * that `ChatPageContextDataContext`, `ChatStateContainer`, etc. are available.
 */
export function ChatInterfaceNative({
  isReady = true,
  onClose,
  onNewChat,
}: {
  isReady?: boolean;
  onClose?: () => void;
  onNewChat?: (() => void) | null;
}): React.JSX.Element {
  const canToggleAuraChat = useAppSelector((s) => s.features.chatAuraIframesEnabled);

  const sidePanelExpandedValue = useMemo<[boolean, Dispatch<SetStateAction<boolean>>]>(
    () => [true, noop],
    []
  );

  const inputFeaturesValue = useMemo(() => ({ fileUpload: false, voiceInput: true }) as const, []);

  return (
    <AuraChatContainer canToggleAuraChat={canToggleAuraChat}>
      <ChatInputFeaturesContext.Provider value={inputFeaturesValue}>
        <ChatLandingCardsVisibleContext.Provider value={false}>
          <SidePanelExpandedContext.Provider value={sidePanelExpandedValue}>
            <ChatHomeButtonVisibleContext.Provider value={false}>
              <ChatContextSelectorVisibleContext.Provider value={true}>
                <PageHeader>
                  <StyledAutoAwesomeIcon />
                  <PageHeaderTitle>Discover Insights</PageHeaderTitle>
                  {onClose && (
                    <CloseBtn onClick={onClose} title="Hide chat">
                      <X size={16} />
                    </CloseBtn>
                  )}
                </PageHeader>
                {isReady ? (
                  <>
                    <HeaderRow>
                      <ChatHeader disableContextDisplay={true} disableTabs={true} />
                      {onNewChat && <NewChatButton onClick={onNewChat} />}
                    </HeaderRow>
                    <StyledChatInstance isSidePanel />
                  </>
                ) : (
                  <SkeletonContainer>
                    <Skeleton variant="rectangular" height={40} />
                    <Skeleton variant="rectangular" height={60} style={{ marginTop: 16 }} />
                    <Skeleton variant="rectangular" height={60} style={{ marginTop: 8 }} />
                    <Skeleton variant="rectangular" height={60} style={{ marginTop: 8 }} />
                  </SkeletonContainer>
                )}
              </ChatContextSelectorVisibleContext.Provider>
            </ChatHomeButtonVisibleContext.Provider>
          </SidePanelExpandedContext.Provider>
        </ChatLandingCardsVisibleContext.Provider>
      </ChatInputFeaturesContext.Provider>
    </AuraChatContainer>
  );
}

const StyledChatInstance = styled(ChatInstance)({
  height: `calc(100% - 90px)`, // Estimate based on header + padding, adjust as needed
});

const SkeletonContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: '12px 8px',
  flex: 1,
});

function NewChatButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <Tooltip title="New Chat">
      <StyledNewChatButton onClick={onClick}>
        <PlusCircle size={20} />
      </StyledNewChatButton>
    </Tooltip>
  );
}

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const PageHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 8px 0',
  marginBottom: '8px',
});

const PageHeaderTitle = styled.span({
  fontSize: 16,
  fontWeight: 600,
  color: '#1a1a1a',
  flex: 1,
});

const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  '& > *:first-child': {
    flex: 1,
    minWidth: 0,
  },
});

const CloseBtn = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  color: theme.palette.gray[400],
  padding: '4px',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.gray[100],
    color: theme.palette.gray[700],
  },
}));

const StyledNewChatButton = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '6px',
  marginRight: '8px',
  borderRadius: '4px',
  color: theme.palette.primary.main,
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,
  },
}));
